"use client";

import { Button } from "@heroui/button";
import { addToast, closeToast } from "@heroui/toast";
import { usePathname } from "next/navigation";
import { useMemo } from "react";

import type { Translator } from "@/lib/i18n/translations";
import type { LibraryStore, MutationError } from "@/lib/library/store";
import type {
  LibraryRestore,
  MovieId,
  MovieLibraryEntry,
} from "@/types/library";
import { trackMovie } from "@/lib/analytics";
import { useLibraryStore } from "@/lib/hooks/useLibrary";
import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { createTranslator } from "@/lib/i18n/translations";
import { getEntry } from "@/lib/library/state";

/** The movie an action is about (the title is used in toasts and analytics). */
export interface LibraryMovieRef {
  id: MovieId;
  title: string;
}

/**
 * Library changes with the user-facing extras: error toasts, "Undo" for
 * removals and analytics. Each resolves to whether the change was saved.
 */
export interface LibraryActions {
  addToWatchlist(movie: LibraryMovieRef): Promise<boolean>;
  removeFromWatchlist(movie: LibraryMovieRef): Promise<boolean>;
  /** Also takes the movie off the watchlist. */
  markWatched(movie: LibraryMovieRef): Promise<boolean>;
  /** Also deletes the rating: ask the user first when the movie is rated. */
  unmarkWatched(movie: LibraryMovieRef): Promise<boolean>;
  /** Also marks the movie watched. */
  rate(movie: LibraryMovieRef, rating: number): Promise<boolean>;
  removeRating(movie: LibraryMovieRef): Promise<boolean>;
}

export const UNDO_TOAST_TIMEOUT_MS = 8000;

function describeError(t: Translator, error: MutationError): string {
  switch (error) {
    case "unauthenticated":
      return t("library.toast.errorUnauthenticated");
    case "network":
      return t("library.toast.errorNetwork");
    default:
      return t("library.toast.errorFailed");
  }
}

/** Report the analytics events for a movie going from `from` to `to`. */
function trackTransition(
  movie: LibraryMovieRef,
  from: MovieLibraryEntry,
  to: LibraryRestore,
) {
  if (to.watchlist && !from.watchlist) {
    trackMovie.addedToWatchlist(movie.id, movie.title);
  }
  if (to.watchlist === null && from.watchlist) {
    trackMovie.removedFromWatchlist(movie.id, movie.title);
  }
  if (to.watched && !from.watched) {
    trackMovie.markedAsWatched(movie.id, movie.title);
  }
  if (to.watched === null && from.watched) {
    trackMovie.unmarkedAsWatched(movie.id, movie.title);
  }
  if (to.rating && to.rating.rating !== from.rating?.rating) {
    trackMovie.rated(movie.id, movie.title, to.rating.rating);
  }
}

export function createLibraryActions(
  store: LibraryStore,
  t: Translator,
): LibraryActions {
  const showError = (error: MutationError, title: string) => {
    addToast({
      title,
      description: describeError(t, error),
      color: "danger",
    });
  };

  /** A confirmation with an "Undo" button that restores `prior`. */
  const showUndo = (
    title: string,
    movie: LibraryMovieRef,
    prior: LibraryRestore,
  ) => {
    let key: string | null = null;
    let used = false;

    const undo = async () => {
      if (used) return;
      used = true;
      if (key) closeToast(key);

      const before = getEntry(store.getState().library, movie.id);
      const outcome = await store.restore(movie.id, prior);

      if (outcome.ok) {
        trackTransition(movie, before, prior);
      } else {
        showError(outcome.error, t("library.toast.undoFailed"));
      }
    };

    key = addToast({
      title,
      description: movie.title,
      timeout: UNDO_TOAST_TIMEOUT_MS,
      endContent: (
        <Button
          className="shrink-0 font-semibold"
          color="primary"
          size="sm"
          variant="flat"
          onPress={() => void undo()}
        >
          {t("library.toast.undo")}
        </Button>
      ),
    });
  };

  const failed = (error: MutationError) => {
    showError(error, t("library.toast.errorTitle"));

    return false;
  };

  return {
    async addToWatchlist(movie) {
      const outcome = await store.addToWatchlist(movie.id);

      if (!outcome.ok) return failed(outcome.error);
      trackMovie.addedToWatchlist(movie.id, movie.title);

      return true;
    },

    async removeFromWatchlist(movie) {
      const outcome = await store.removeFromWatchlist(movie.id);

      if (!outcome.ok) return failed(outcome.error);
      trackMovie.removedFromWatchlist(movie.id, movie.title);
      showUndo(t("library.toast.removedFromWatchlist"), movie, outcome.prior);

      return true;
    },

    async markWatched(movie) {
      const outcome = await store.markWatched(movie.id);

      if (!outcome.ok) return failed(outcome.error);
      trackMovie.markedAsWatched(movie.id, movie.title);

      // It left the watchlist as a side effect: say so, and offer to undo.
      if (outcome.prior.watchlist) {
        showUndo(t("library.toast.movedToWatched"), movie, outcome.prior);
      }

      return true;
    },

    async unmarkWatched(movie) {
      const outcome = await store.unmarkWatched(movie.id);

      if (!outcome.ok) return failed(outcome.error);
      trackMovie.unmarkedAsWatched(movie.id, movie.title);
      showUndo(
        outcome.prior.rating
          ? t("library.toast.unmarkedWatchedAndRating")
          : t("library.toast.unmarkedWatched"),
        movie,
        outcome.prior,
      );

      return true;
    },

    async rate(movie, rating) {
      const outcome = await store.rate(movie.id, rating);

      if (!outcome.ok) return failed(outcome.error);
      trackMovie.rated(movie.id, movie.title, rating);
      addToast({
        title: outcome.prior.watched
          ? t("library.toast.rated", { rating })
          : t("library.toast.ratedAndWatched", { rating }),
        description: movie.title,
        color: "success",
      });

      return true;
    },

    async removeRating(movie) {
      const outcome = await store.removeRating(movie.id);

      if (!outcome.ok) return failed(outcome.error);
      showUndo(t("library.toast.ratingRemoved"), movie, outcome.prior);

      return true;
    },
  };
}

/** Library actions for buttons, with toasts, undo and analytics. */
export function useLibraryActions(): LibraryActions {
  const store = useLibraryStore();
  const lang = getLanguageFromPathname(usePathname());

  return useMemo(
    () => createLibraryActions(store, createTranslator(lang)),
    [store, lang],
  );
}
